import React, { useMemo, useState } from "react";
import { DataTable, ErrorBox, Icon, LoadingBox, Modal, PageShell, Stat } from "../admin/_adminApi";
import { formatDate, formatPkr, useReceipts } from "./useReceipts";

const patientName = (r) => {
  const p = r?.patient;
  if (!p || typeof p !== "object") return "—";
  return [p.first_name, p.last_name].filter(Boolean).join(" ") || p.username || p.email || "—";
};

// Saved through FormData, so the backend can hand this back as a JSON string.
const medicinesOf = (r) => {
  const m = r?.medicines;
  if (Array.isArray(m)) return m;
  if (typeof m !== "string") return [];
  try {
    const parsed = JSON.parse(m);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const Receipts = () => {
  const { receipts, loading, error } = useReceipts();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return receipts;
    return receipts.filter((r) =>
      patientName(r).toLowerCase().includes(q)
      || medicinesOf(r).some((m) => String(m?.name || "").toLowerCase().includes(q)));
  }, [receipts, query]);

  const now = new Date();
  const monthTotal = receipts
    .filter((r) => {
      const d = new Date(r.created_at);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    })
    .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  return (
    <PageShell title="Medicine Receipts" subtitle="Medicines dispensed to patients. Select View to see what each receipt contains.">
      <div className="ad-stats">
        <Stat tone="teal" icon={Icon.wallet} label="This month" value={formatPkr(monthTotal)} />
        <Stat tone="blue" icon={Icon.file} label="Total receipts" value={receipts.length} />
      </div>
      <div className="border rounded-full flex" style={{ maxWidth: 400, marginBottom: 16 }}>
        <input type="search" placeholder="Search patient or medicine" aria-label="Search receipts"
          value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      {error ? <ErrorBox error={error} /> : loading ? <LoadingBox /> : (
        <div className="ad-card">
          <DataTable
            rows={rows}
            emptyText={query ? "No receipts match your search." : "No receipts yet."}
            columns={[
              { label: "Patient", render: (r) => <span className="t-strong">{patientName(r)}</span> },
              { label: "Total amount", render: (r) => formatPkr(r.amount) },
              { label: "Date", render: (r) => <span style={{ whiteSpace: "nowrap" }}>{formatDate(r.created_at)}</span> },
              { label: "", align: "right", render: (r) => (
                <button type="button" className="ad-btn ghost sm" onClick={() => setOpen(r)}>View</button>
              ) },
            ]}
          />
        </div>
      )}
      {open && (
        <Modal
          title="Receipt"
          icon={Icon.file}
          onClose={() => setOpen(null)}
          desc={<>{patientName(open)} · {formatDate(open.created_at)}</>}
          footer={<button type="button" className="ad-btn ghost" onClick={() => setOpen(null)}>Close</button>}
        >
          <DataTable
            rows={medicinesOf(open)}
            emptyText="No medicines were recorded on this receipt."
            columns={[
              { label: "Medicine", render: (m) => m?.name || "—" },
              { label: "Quantity", render: (m) => m?.quantity ?? "—" },
              { label: "Amount", align: "right", render: (m) => formatPkr(m?.amount) },
            ]}
          />
          <p style={{ margin: "14px 0 0", textAlign: "right", fontWeight: 700 }}>Total: {formatPkr(open.amount)}</p>
        </Modal>
      )}
    </PageShell>
  );
};

export default Receipts;
